import { useRef, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCalendarDays,
  formatDate,
  isSameDay,
  getISOWeek,
  getLunarInfo,
  getHoliday,
} from '@daynote/utils';
import type { DayNote } from '@daynote/shared-types';

interface Props {
  month: string;
  dayNotesByDate: Record<string, DayNote>;
  showLunar?: boolean;
  showHoliday?: boolean;
  weekStart?: 0 | 1;
  /** 屏幕内完整显示的周行数，超出时滚动 */
  maxRows?: number;
  onDayDoubleClick: (date: string) => void;
}

export function MonthGrid({
  month,
  dayNotesByDate,
  showLunar = true,
  showHoliday = true,
  weekStart = 1,
  maxRows = 5,
  onDayDoubleClick,
}: Props) {
  const { t } = useTranslation();
  const today = new Date();
  const days = getCalendarDays(month, weekStart);
  const [, m] = month.split('-').map(Number);

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // 计算每行高度：用容器高度 ÷ maxRows
  const containerRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setRowHeight(h / maxRows);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxRows]);

  const rowCount = weeks.length;
  // 行数 ≤ maxRows：1fr 均分高度填满容器；行数 > maxRows：固定每行高度，超出部分滚动
  const gridAutoRows = rowCount <= maxRows
    ? '1fr'
    : rowHeight ? `${rowHeight}px` : '1fr';
  const gridHeight = rowCount <= maxRows ? '100%' : 'auto';

  return (
    <div ref={containerRef} className="flex-1 overflow-auto min-h-0">
      <div
        className="grid"
        style={{
          gridTemplateColumns: '32px repeat(7, 1fr)',
          gridAutoRows,
          height: gridHeight,
        }}
      >
        {weeks.map((week, wi) => {
          const weekNum = getISOWeek(week[0]!);
          return (
            <div key={`week-${wi}`} className="contents">
              <div className="flex items-center justify-center font-display text-[11px] text-white/30 border-r border-b border-[var(--grid-line)] bg-black/20">
                {weekNum}
              </div>
              {week.map((day) => {
                const dateStr = formatDate(day);
                const isCurrentMonth = day.getMonth() + 1 === m;
                const isToday = isSameDay(day, today);
                const isFirstOfMonth = day.getDate() === 1;
                const lunar = getLunarInfo(dateStr);
                const holiday = showHoliday ? getHoliday(dateStr) : null;
                const dn = dayNotesByDate[dateStr];

                return (
                  <div
                    key={dateStr}
                    onDoubleClick={() => onDayDoubleClick(dateStr)}
                    className={`relative p-2 border-r border-b border-[var(--grid-line)] cursor-pointer overflow-hidden transition-colors hover:bg-[var(--panel-hover)] ${
                      !isCurrentMonth ? 'opacity-45' : ''
                    } ${isToday ? 'bg-[var(--panel-today)] outline outline-2 outline-daynote-accent z-10' : ''}`}
                  >
                    {isFirstOfMonth && (
                      <span className="absolute -top-2.5 left-2 font-display text-base italic text-daynote-accent bg-[var(--bg-primary)] px-2 py-0.5 rounded-sm tracking-wide">
                        {t(`months.${day.getMonth() + 1}`)}
                      </span>
                    )}

                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <span className="font-display text-base font-medium text-daynote-text">
                        {day.getDate()}
                      </span>
                      {showLunar && (
                        <span className="text-[11px] text-daynote-text/55 tracking-wide">
                          {lunar.lunarDay}
                        </span>
                      )}
                      {holiday && (
                        <>
                          <span className="text-[11px] text-daynote-warm font-medium tracking-wide">
                            {holiday.name}
                          </span>
                          <span
                            className={`inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold text-white ${
                              holiday.type === 'rest' ? 'bg-daynote-rest' : 'bg-daynote-work'
                            }`}
                          >
                            {holiday.type === 'rest' ? '休' : '班'}
                          </span>
                        </>
                      )}
                      {showLunar && lunar.solarTerm && !holiday && (
                        <span className="text-[11px] text-daynote-warm font-medium">{lunar.solarTerm}</span>
                      )}
                    </div>

                    {dn && dn.items.length > 0 && (
                      <ul className="text-xs space-y-0.5">
                        {dn.items.map((it, idx) => (
                          <li
                            key={it.id}
                            className={`flex gap-1.5 items-start truncate ${it.isDone ? 'line-through opacity-45' : ''}`}
                          >
                            {it.color ?? it.group?.color ? (
                              <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                                style={{ background: it.color ?? it.group?.color ?? undefined }}
                              />
                            ) : (
                              <span className="font-display italic text-daynote-muted text-[13px]">
                                {idx + 1}.
                              </span>
                            )}
                            <span className="truncate">
                              {it.group && (
                                <span className="text-daynote-accent">[{it.group.abbr}] </span>
                              )}
                              {it.content}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
